// ─────────────────────────────────────────────
//  SchedulePage — the INTERNAL scheduling module (/schedule).
//  In-app replacement for the external Google Sheet, fully wired to the
//  app's own data:
//    • EVERY room of each facility is listed and EXPANDED by default (rooms
//      without a case can be collapsed manually).
//    • Surgeon + Procedure are DROPDOWNS fed by the facility's own
//      procedure_refs bag (live — new values appear the moment they're
//      remembered anywhere in the app).
//    • Attending + CRNA/AA dropdowns come from the system staff, that
//      facility's own people listed first.
//    • CRNA Shift Time presets: 3/5/7/9/11 PM and 24 Hours (24 = on-call).
//    • The banner up top shows WHICH midnight source is live (Internal
//      Schedule vs Google Sheet); the switch lives in Setup ▸ Google Sheet
//      Import (admin).
//  Rows live in `schedule_rows` (admin/Scheduler only) in the exact draft
//  shape the midnight import feeds to buildBoardWrites.
// ─────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "../firebase/sdk";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useFacilities } from "../hooks/useFacilities";
import { useFacility } from "../hooks/useFacility";
import { useStaff } from "../hooks/useStaff";
import {
  listenToScheduleRows, addScheduleRow, updateScheduleRow, deleteScheduleRow,
  listenToProcedureRefs, addSpecialty, addSpecialtyProcedure,
  listenToScheduleDay, setScheduleDayClosed,
  createAssignment, updateAssignmentRow, setDailyRoster,
} from "../firebase/firestore";
import { splitForClose } from "../domain/internalSchedule";
import { linkProviderChains } from "../features/sheet-import/linkCrnaChains";
import { buildBoardWrites } from "../features/sheet-import/buildBoardWrites";
import { mergeRosterPatch } from "../features/sheet-import/parseGlobalSheet";
import { assignmentForRoom } from "../domain/gridTemplate";
import { resolveFacilityStatus } from "../domain/facilityRules";
import { facilityLabel } from "../domain/facilities";
import { sortPoolByPrimaryFacility } from "../domain/staff";
import { START_TIME_OPTIONS, END_TIME_OPTIONS } from "../components/assignmentRow.cells";
import NavMenu from "../components/NavMenu";
import Avatar from "../components/Avatar";
import SignOutButton from "../components/SignOutButton";
import AccessDenied from "../components/AccessDenied";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";

const { color } = tokens;

// CRNA shift presets (maps onto the existing shift-code taxonomy: the number
// is the go-home hour; "24" is the around-the-clock on-call shift).
// Starter specialty list — merged with whatever the facility has remembered.
export const DEFAULT_SPECIALTIES = [
  "Bariatric", "Cardiac", "ENT", "General", "GYN", "Neuro",
  "Orthopedic", "Pediatric", "Plastics", "Urology", "Vascular",
];

export const SHIFT_TIME_OPTIONS = [
  { value: "3",  label: "3 PM" },
  { value: "5",  label: "5 PM" },
  { value: "7",  label: "7 PM" },
  { value: "9",  label: "9 PM" },
  { value: "11", label: "11 PM" },
  { value: "24", label: "24 Hours" },
];

function tomorrowKey() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const normRoom = (v) => String(v || "").trim().toLowerCase().replace(/\s+/g, "");
const cell = { padding: "3px 4px", verticalAlign: "middle" };
const inp = { width: "100%", boxSizing: "border-box", padding: "6px 8px", borderRadius: 6, border: `1px solid ${color.border.default}`, fontSize: 12, fontFamily: "inherit", background: "#fff", color: color.text.primary };
const th = { textAlign: "left", fontSize: 10, fontWeight: 800, color: color.text.muted, textTransform: "uppercase", letterSpacing: ".5px", padding: "4px 4px" };

// A select whose options come from a live list but always includes the row's
// current value (so a legacy/removed value still displays instead of blanking).
function RefSelect({ value, options, placeholder, onChange, aria, disabled = false }) {
  const opts = useMemo(() => {
    const set = [...options];
    if (value && !set.includes(value)) set.unshift(value);
    return set;
  }, [options, value]);
  return (
    <select style={inp} value={value || ""} onChange={(e) => onChange(e.target.value)} aria-label={aria} disabled={disabled}>
      <option value="">{placeholder}</option>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

const ADD_NEW = "__add_new__";

function RoomRow({ room, row, facility, date, doctors, crnas, refs, collapsed, onToggle, readOnly }) {
  const toast = useToast();

  const patch = async (data) => {
    if (readOnly) return;
    try {
      if (row?.id) {
        await updateScheduleRow(row.id, data);
      } else {
        await addScheduleRow({
          facilityId: facility.id, date, orRoom: room.name,
          surgeon: "", procedure: "", startTime: "", endTime: "",
          attendingId: null, attendingName: "", callOrder: null, isAIC: false,
          ahpId: null, ahpName: "", ahpShiftCode: null, onCall: false,
          ...data,
        });
      }
    } catch (e) {
      console.error("[Schedule] save failed:", e);
      toast.error("Could not save — check your connection.");
    }
  };

  const clearRow = async () => {
    if (!row?.id) return;
    try {
      await deleteScheduleRow(row.id);
    } catch (e) {
      console.error("[Schedule] clear failed:", e);
      toast.error("Could not clear the room.");
    }
  };

  const setAttending = (id) => {
    const d = doctors.find((x) => x.id === id);
    patch({ attendingId: id || null, attendingName: d?.name || "" });
  };
  const setAhp = (id) => {
    const p = crnas.find((x) => x.id === id);
    patch({ ahpId: id || null, ahpName: p?.name || "" });
  };
  // Shift preset: the value IS the go-home shift code; "24 Hours" also flags
  // the row on-call (no case time needed — covers 7 AM → 7 AM).
  const setShift = (v) => patch({ ahpShiftCode: v || null, onCall: v === "24" });

  // Specialty-first procedures: the Procedure list shows only the selected
  // specialty's procedures; "＋ Add new…" saves a value on the fly (it joins
  // that specialty's list for every future schedule).
  const specialties = useMemo(
    () => [...new Set([...DEFAULT_SPECIALTIES, ...(refs.specialties || [])])].sort(),
    [refs.specialties]
  );
  const specialtyProcedures = row?.specialty
    ? (refs.proceduresBySpecialty?.[row.specialty] || [])
    : refs.procedures;
  const setSpecialty = async (v) => {
    if (v === ADD_NEW) {
      const name = window.prompt("New specialty name:");
      if (!name?.trim()) return;
      await addSpecialty(facility.id, name.trim());
      patch({ specialty: name.trim(), procedure: "" });
      return;
    }
    patch({ specialty: v, procedure: "" }); // procedures are per-specialty
  };
  const setProcedure = async (v) => {
    if (v === ADD_NEW) {
      if (!row?.specialty) { toast.info("Pick a specialty first."); return; }
      const name = window.prompt(`New ${row.specialty} procedure:`);
      if (!name?.trim()) return;
      await addSpecialtyProcedure(facility.id, row.specialty, name.trim());
      patch({ procedure: name.trim() });
      return;
    }
    patch({ procedure: v });
  };

  const hasCase = !!(row && (row.surgeon || row.procedure || row.startTime || row.attendingId || row.ahpId));

  if (collapsed) {
    return (
      <tr style={{ borderTop: `1px solid ${color.border.default}`, background: color.surface.raised }}>
        <td colSpan={12} style={{ ...cell, padding: "6px 8px" }}>
          <button
            onClick={onToggle}
            aria-expanded={false}
            aria-label={`Expand ${room.name}`}
            style={{ background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12, color: color.text.secondary, display: "flex", gap: 8, alignItems: "center" }}
          >
            <span aria-hidden="true">▸</span>
            <strong style={{ color: color.text.primary }}>{room.name}</strong>
            <span>{hasCase ? `${row.surgeon || "case"} · ${row.startTime || "—"}–${row.endTime || "—"}` : "no case scheduled"}</span>
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={{ borderTop: `1px solid ${color.border.default}` }}>
      <td style={{ ...cell, whiteSpace: "nowrap" }}>
        <button
          onClick={onToggle}
          aria-expanded
          aria-label={`Collapse ${room.name}`}
          style={{ background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 800, color: color.text.primary, display: "flex", gap: 6, alignItems: "center" }}
        >
          <span aria-hidden="true">▾</span>{room.name}
        </button>
      </td>
      <td style={cell}>
        <RefSelect value={row?.surgeon} options={refs.surgeons} placeholder="— surgeon —" aria={`Surgeon for ${room.name}`} onChange={(v) => patch({ surgeon: v })} disabled={readOnly} />
      </td>
      <td style={cell}>
        <select style={inp} value={row?.specialty || ""} onChange={(e) => setSpecialty(e.target.value)} aria-label={`Specialty for ${room.name}`} disabled={readOnly}>
          <option value="">— specialty —</option>
          {specialties.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
          <option value={ADD_NEW}>＋ Add new specialty…</option>
        </select>
      </td>
      <td style={cell}>
        <select style={inp} value={row?.procedure || ""} onChange={(e) => setProcedure(e.target.value)} aria-label={`Procedure for ${room.name}`} disabled={readOnly}>
          <option value="">— procedure —</option>
          {(row?.procedure && !specialtyProcedures.includes(row.procedure) ? [row.procedure] : [])
            .concat([...specialtyProcedures].sort())
            .map((p) => <option key={p} value={p}>{p}</option>)}
          <option value={ADD_NEW}>＋ Add new procedure…</option>
        </select>
      </td>
      <td style={cell}>
        <select style={inp} value={row?.startTime || ""} onChange={(e) => patch({ startTime: e.target.value })} aria-label={`Start time for ${room.name}`} disabled={readOnly}>
          <option value="">—</option>
          {START_TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </td>
      <td style={cell}>
        <select style={inp} value={row?.endTime || ""} onChange={(e) => patch({ endTime: e.target.value })} aria-label={`End time for ${room.name}`} disabled={readOnly}>
          <option value="">—</option>
          {END_TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </td>
      <td style={cell}>
        <select style={inp} value={row?.attendingId || ""} onChange={(e) => setAttending(e.target.value)} aria-label={`Attending for ${room.name}`} disabled={readOnly}>
          <option value="">— doctor —</option>
          {doctors.map((d) => <option key={d.id} value={d.id}>{d.shortCode ? `${d.shortCode} — ` : ""}{d.name}</option>)}
        </select>
      </td>
      <td style={{ ...cell, textAlign: "center" }}>
        <input type="checkbox" checked={!!row?.isAIC} onChange={(e) => patch({ isAIC: e.target.checked })} aria-label={`AIC in ${room.name}`} disabled={readOnly} />
      </td>
      <td style={cell}>
        <input style={inp} type="number" min="1" max="30" value={row?.callOrder ?? ""} onChange={(e) => patch({ callOrder: e.target.value === "" ? null : Number(e.target.value) })} aria-label={`Call order for ${room.name}`} disabled={readOnly} />
      </td>
      <td style={cell}>
        <select style={inp} value={row?.ahpId || ""} onChange={(e) => setAhp(e.target.value)} aria-label={`CRNA/AA for ${room.name}`} disabled={readOnly}>
          <option value="">— CRNA/AA —</option>
          {crnas.map((p) => <option key={p.id} value={p.id}>{p.name}{p.subRole && p.subRole !== "crna" ? ` · ${p.subRole.toUpperCase()}` : ""}</option>)}
        </select>
      </td>
      <td style={cell}>
        <select style={inp} value={row?.ahpShiftCode ?? ""} onChange={(e) => setShift(e.target.value)} aria-label={`Shift time for ${room.name}`} disabled={readOnly}>
          <option value="">— shift —</option>
          {SHIFT_TIME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>
      <td style={{ ...cell, textAlign: "center" }}>
        {row?.id && !readOnly && (
          <button
            onClick={clearRow}
            aria-label={`Clear the case in ${room.name}`}
            title="Clear this room's case"
            style={{ background: "#dc262610", border: "1px solid #dc262655", color: color.accent.error, borderRadius: 6, width: 26, height: 26, cursor: "pointer" }}
          >
            ✕
          </button>
        )}
      </td>
    </tr>
  );
}

function FacilitySchedule({ facility, date, doctors, crnas, readOnly }) {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [refs, setRefs] = useState({ surgeons: [], procedures: [] });
  // Rooms EXPANDED by default; collapsing is a manual, per-room choice.
  const [collapsedRooms, setCollapsedRooms] = useState(() => new Set());

  useEffect(() => {
    getDocs(query(collection(db, "rooms"), where("facilityId", "==", facility.id)))
      .then((snap) =>
        setRooms(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { numeric: true }))
        )
      )
      .catch((e) => console.error("[Schedule] rooms load failed:", e));
  }, [facility.id]);

  useEffect(() => {
    const unsub = listenToScheduleRows(
      facility.id, date,
      (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("[Schedule] rows listener failed:", e)
    );
    return unsub;
  }, [facility.id, date]);

  // Live surgeon/procedure lists — a value remembered anywhere in the app
  // shows up here immediately.
  useEffect(() => {
    const unsub = listenToProcedureRefs(
      facility.id,
      (r) => setRefs({ surgeons: [...r.surgeons].sort(), procedures: [...r.procedures].sort() }),
      (e) => console.error("[Schedule] procedure_refs listener failed:", e)
    );
    return unsub;
  }, [facility.id]);

  // Facility-first ordering for this SECTION's people dropdowns.
  const facDoctors = useMemo(() => sortPoolByPrimaryFacility(doctors, facility.id), [doctors, facility.id]);
  const facCrnas = useMemo(() => sortPoolByPrimaryFacility(crnas, facility.id), [crnas, facility.id]);

  const rowForRoom = (room) => rows.find((r) => normRoom(r.orRoom) === normRoom(room.name)) || null;
  const benchRows = rows.filter((r) => !String(r.orRoom || "").trim());

  const toggleRoom = (name) =>
    setCollapsedRooms((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const addBench = async () => {
    try {
      await addScheduleRow({
        facilityId: facility.id, date, orRoom: "",
        surgeon: "", procedure: "", startTime: "", endTime: "",
        attendingId: null, attendingName: "", callOrder: null, isAIC: false,
        ahpId: null, ahpName: "", ahpShiftCode: null, onCall: false,
      });
    } catch (e) {
      console.error("[Schedule] add bench failed:", e);
      toast.error("Could not add a bench row.");
    }
  };
  const setBench = async (r, data) => {
    try {
      await updateScheduleRow(r.id, data);
    } catch (e) {
      console.error("[Schedule] bench save failed:", e);
      toast.error("Could not save.");
    }
  };

  const scheduled = rows.filter((r) => String(r.orRoom || "").trim()).length;

  return (
    <div style={{ ...s.panel, overflowX: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 10, flexWrap: "wrap" }}>
        <div style={s.panelTitle}>
          🏥 {facilityLabel(facility.id) !== facility.id ? facilityLabel(facility.id) : facility.label || facility.name || facility.id}
          <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 700, color: color.text.muted }}>
            {scheduled} of {rooms.length} rooms scheduled
          </span>
        </div>
        {!readOnly && (
          <button style={s.ghostBtn} onClick={addBench}>＋ On-call / bench row (no room)</button>
        )}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 108 }}>Room</th>
            <th style={{ ...th, width: 140 }}>Surgeon</th>
            <th style={{ ...th, width: 120 }}>Specialty</th>
            <th style={{ ...th, width: 170 }}>Procedure</th>
            <th style={{ ...th, width: 88 }}>Start</th>
            <th style={{ ...th, width: 88 }}>End</th>
            <th style={{ ...th, width: 165 }}>Attending</th>
            <th style={{ ...th, width: 42 }} title="Anesthesia In Charge">AIC</th>
            <th style={{ ...th, width: 54 }} title="Call order">Call#</th>
            <th style={{ ...th, width: 165 }}>CRNA / AA</th>
            <th style={{ ...th, width: 96 }} title="CRNA shift time">Shift Time</th>
            <th style={{ ...th, width: 34 }} />
          </tr>
        </thead>
        <tbody>
          {rooms.map((room) => (
            <RoomRow
              key={room.id}
              room={room}
              row={rowForRoom(room)}
              facility={facility}
              date={date}
              doctors={facDoctors}
              crnas={facCrnas}
              refs={refs}
              collapsed={collapsedRooms.has(room.name)}
              onToggle={() => toggleRoom(room.name)}
              readOnly={readOnly}
            />
          ))}
          {benchRows.map((r) => (
            <tr key={r.id} style={{ borderTop: `1px dashed ${color.border.default}`, background: "#f59e0b0d" }}>
              <td style={{ ...cell, fontSize: 11, fontWeight: 800, color: "#7a4f00", whiteSpace: "nowrap" }}>☕ Bench</td>
              <td style={cell} colSpan={5} />
              <td style={cell} colSpan={2} />
              <td style={cell}>
                <select style={inp} value={r.ahpId || ""} onChange={(e) => {
                  const p = facCrnas.find((x) => x.id === e.target.value);
                  setBench(r, { ahpId: e.target.value || null, ahpName: p?.name || "" });
                }} aria-label="Bench CRNA/AA" disabled={readOnly}>
                  <option value="">— CRNA/AA —</option>
                  {facCrnas.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </td>
              <td style={cell}>
                <select style={inp} value={r.ahpShiftCode ?? ""} onChange={(e) => setBench(r, { ahpShiftCode: e.target.value || null, onCall: e.target.value === "24" })} aria-label="Bench shift time" disabled={readOnly}>
                  <option value="">— shift —</option>
                  {SHIFT_TIME_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </td>
              <td style={{ ...cell, textAlign: "center" }}>
                {!readOnly && (
                  <button onClick={() => deleteScheduleRow(r.id)} aria-label="Remove bench row"
                    style={{ background: "#dc262610", border: "1px solid #dc262655", color: color.accent.error, borderRadius: 6, width: 26, height: 26, cursor: "pointer" }}>✕</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SchedulePage() {
  const { user, isDoctor, isAdmin, canManageSchedule } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const allFacilities = useFacilities();
  const { doctors, providers } = useStaff();
  const [date, setDate] = useState(tomorrowKey());
  // Which midnight source is LIVE — read from the same flag the function uses.
  const { facility: defaultFacility } = useFacility("default");
  const internalActive = defaultFacility?.useInternalSchedule === true;
  // Close/Reopen state for THIS date (one lock across all facilities).
  const [day, setDay] = useState({ closed: false });
  const [closing, setClosing] = useState(false);
  // ⚠️ GATED ON THE SAME PERMISSION AS THE PAGE. The AccessDenied return below
  // cannot help here: it is a conditional return, so it must come AFTER every
  // hook, which means this effect ran for people who never got to see the
  // page. `schedule_days` is scheduler-only, so every visit by a doctor or a
  // CRNA opened a listener the rules immediately denied — a guaranteed
  // permission-denied and a red console error on a screen that had already
  // decided, correctly, to show them nothing. Gate the SUBSCRIPTION, not just
  // the render.
  useEffect(() => {
    if (!canManageSchedule) return undefined;
    const unsub = listenToScheduleDay(date, setDay, (e) =>
      console.error("[Schedule] day listener failed:", e)
    );
    return unsub;
  }, [date, canManageSchedule]);
  const closed = day?.closed === true;

  // CLOSE: finalize the date — delete empty rows (they never become cards),
  // apply COMPLETE rows to each facility's assignments/roster (upsert; safe
  // to close → reopen → close again), lock editing. Admin-only: the board
  // writes are admin-gated across facilities.
  const facilitiesActive = allFacilities.filter((f) => resolveFacilityStatus(f) === "active");
  const facilitiesList = facilitiesActive.length > 0 ? facilitiesActive : allFacilities;
  const closeSchedule = async () => {
    setClosing(true);
    try {
      let applied = 0, removed = 0;
      for (const f of facilitiesList) {
        const rowsSnap = await getDocs(query(collection(db, "schedule_rows"),
          where("facilityId", "==", f.id), where("date", "==", date)));
        const rows = rowsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const { apply, benches, deletions } = splitForClose(rows);
        for (const r of deletions) await deleteScheduleRow(r.id);
        removed += deletions.length;
        if (apply.length + benches.length === 0) continue;

        const roomsSnap = await getDocs(query(collection(db, "rooms"), where("facilityId", "==", f.id)));
        const rooms = roomsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const chained = linkProviderChains(apply, rooms);
        const unassigned = benches.map((r) => ({ name: r.ahpName || "", ahpId: r.ahpId, shiftCode: r.ahpShiftCode ?? null }));
        const { assignments, roster } = buildBoardWrites(chained, unassigned, []);

        const existingSnap = await getDocs(query(collection(db, "assignments"),
          where("facilityId", "==", f.id), where("date", "==", date)));
        const existing = existingSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        for (const a of assignments) {
          const hit = assignmentForRoom(a.orRoom, existing);
          if (hit?.id) await updateAssignmentRow(hit.id, a);
          else await createAssignment({ facilityId: f.id, date, ...a });
        }
        const boardsSnap = await getDocs(query(collection(db, "daily_boards"),
          where("facilityId", "==", f.id), where("date", "==", date)));
        const patch = mergeRosterPatch(boardsSnap.docs[0]?.data() || {}, roster);
        await setDailyRoster(f.id, date, patch, user?.uid);
        applied += assignments.length;
      }
      await setScheduleDayClosed(date, true, user?.uid);
      toast.success(`Schedule closed — ${applied} case${applied === 1 ? "" : "s"} applied to the boards, ${removed} empty row${removed === 1 ? "" : "s"} removed.`);
    } catch (e) {
      console.error("[Schedule] close failed:", e);
      toast.error("Close failed partway — re-running is safe (rows upsert). " + (e?.message || ""));
    } finally {
      setClosing(false);
    }
  };
  const reopenSchedule = async () => {
    try {
      await setScheduleDayClosed(date, false, user?.uid);
      toast.info("Schedule reopened — edits will apply to the boards when you close it again.");
    } catch (e) {
      console.error("[Schedule] reopen failed:", e);
      toast.error("Could not reopen the schedule.");
    }
  };

  if (!canManageSchedule) {
    return <AccessDenied body="The scheduling module is for administrators and schedulers. Your own day is on the board and in My Rooms." />;
  }

  const facilities = facilitiesList;

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
          <span style={s.logo}>⚕ ATC</span>
          <NavMenu />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: color.text.muted, textTransform: "uppercase" }} htmlFor="sched-date">Schedule date</label>
          <input id="sched-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
            style={{ ...s.select, width: "auto", padding: "6px 10px", fontWeight: 700 }} />
          <Avatar user={user} isDoctor={isDoctor} onClick={() => navigate("/profile")} />
          <SignOutButton />
        </div>
      </div>

      <div style={s.layout}>
        <div style={s.main}>
          {/* ACTIVE-SOURCE indicator — always visible up top. */}
          <div
            role="status"
            style={{
              display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
              padding: "9px 14px", borderRadius: 8, fontSize: 13, fontWeight: 800,
              background: internalActive ? "#10b98114" : "#f59e0b18",
              border: `1px solid ${internalActive ? "#10b98166" : "#f59e0b"}`,
              color: internalActive ? "#0a7d5e" : "#7a4f00",
            }}
          >
            {internalActive
              ? "🟢 Midnight import source: INTERNAL SCHEDULE — what you enter here becomes tomorrow's boards."
              : "📊 Midnight import source: GOOGLE SHEET — this schedule is NOT imported until an admin flips the switch (Setup ▸ Google Sheet Import)."}
          </div>

          <div style={s.tabHeader}>
            <div>
              <div style={s.tabTitle}>
                📅 OR Schedule — {date}
                {closed && (
                  <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 800, color: "#7a4f00", background: "#f59e0b22", border: "1px solid #f59e0b", borderRadius: 9999, padding: "2px 10px", verticalAlign: 3 }}>
                    🔒 CLOSED
                  </span>
                )}
              </div>
              <div style={s.tabSub}>
                {closed
                  ? "This schedule is finalized. Reopen it to add, edit, or change entries — closing again re-applies everything to the boards."
                  : "Every room is listed and expanded; collapse rooms without cases with the ▾ chevron. Closing removes empty rows and transfers complete rows to the daily assignments."}
              </div>
            </div>
            {isAdmin && (
              closed ? (
                <button style={s.primaryBtn} onClick={reopenSchedule}>🔓 Reopen Schedule</button>
              ) : (
                <button style={{ ...s.primaryBtn, background: "#0a7d5e" }} onClick={closeSchedule} disabled={closing}>
                  {closing ? "Closing…" : "🔒 Close Schedule"}
                </button>
              )
            )}
          </div>
          {facilities.map((f) => (
            <FacilitySchedule key={f.id} facility={f} date={date} doctors={doctors} crnas={providers} readOnly={closed} />
          ))}
        </div>
      </div>
    </div>
  );
}
